/**
 * Utility functions for handling Ory Kratos flow nodes.
 *
 * Flows (such as registration, login, verification) returned from Ory Kratos contain nodes; these are the response structure
 * of Ory Kratos that encapsulate form fields. Since we use our own UI / form handling, we have these utility functions to
 * extract relevant data from the flow nodes.
 *
 * These also ensure that the return types are correctly typed, avoiding the need for type assertions.
 */

import {
  BIFROST_ERR_INSUFFICIENT_AAL,
  BIFROST_ERR_SESSION_NOT_PRIVILEGED,
} from '@joinrepublic/bifrost-messages';
import {
  ContinueWith,
  LoginFlow,
  LogoutFlow,
  RecoveryFlow,
  RegistrationFlow,
  SettingsFlow,
  UiNode,
  UiText,
  VerificationFlow,
} from '@ory/kratos-client';
import { isAxiosError } from 'axios';
import type { Merge } from 'react-hook-form';

type InputNodeWithStringValue = Merge<
  UiNode,
  { attributes: { node_type: 'input'; value?: string } }
>;
type FlowWithUi = {
  ui: {
    messages?: UiText[];
    nodes: UiNode[];
  };
};

/**
 * Get a flow input node by name.
 *
 * Kratos flows return nodes representing form fields. We use our own UI, so we just extract some input nodes from the flow
 * and use them to populate our forms as needed.
 *
 * @param nodes The flow nodes to search.
 * @param name The name of the input node to find.
 * @returns The found input node or undefined.
 */
export function getFlowInputNode(
  nodes: UiNode[] | undefined,
  name: string
): InputNodeWithStringValue | undefined {
  return nodes?.find(
    (node) =>
      node.attributes.node_type === 'input' &&
      node.attributes.name === name &&
      (typeof node.attributes.value === 'string' ||
        typeof node.attributes.value === 'undefined')
  );
}

export function getAttrById<
  N extends UiNode,
  T extends UiNode['attributes']['node_type']
>(
  nodes: UiNode[] | undefined,
  id: string,
  type: T
): Extract<N['attributes'], { node_type: T }> | undefined {
  return nodes?.find(
    (node): node is N =>
      'id' in node.attributes &&
      node.attributes.id === id &&
      node.attributes.node_type === type
  )?.attributes as any;
}

/**
 * Get a `continue_with` node by action type.
 *
 * A `continue_with` node represents the next action, such as a redirect to the initial return_to URL after
 * completing a flow, or continuing with a different flow, such as going to verification after registration.
 *
 * @param continueWith The continue_with nodes to search.
 * @param action_type The action type of the continue_with node to find.
 * @returns The found continue_with node or undefined.
 */
export function getContinueWithNode<A extends ContinueWith['action']>(
  continueWith: ContinueWith[] | undefined,
  action_type: A
) {
  return continueWith?.find(
    (node): node is Extract<ContinueWith, { action: A }> =>
      node.action === action_type
  );
}

function getFlowMessages(flow: FlowWithUi | undefined): UiText[] {
  if (!flow?.ui) return [];

  return [
    ...(flow.ui.messages ?? []),
    ...(flow.ui.nodes ?? []).flatMap((node) => node.messages ?? []),
  ];
}

function hasFlowMessageId(
  flow: FlowWithUi | undefined,
  messageId: number
): boolean {
  return getFlowMessages(flow).some((message) => message.id === messageId);
}

/**
 * True when a registration flow was initiated by a social (OIDC) login.
 *
 * We do not allow social sign-ups. When a user signs in via OIDC with an account
 * that isn't linked to any identity, Bifrost converts the login into a registration
 * flow whose active strategy is `oidc` (the backend then rejects it). We use this
 * to bounce the user back to login, regardless of the specific rejection message.
 */
export function isSocialLoginRejectedFlow(
  flow: Pick<RegistrationFlow, 'active'> | undefined
): boolean {
  return flow?.active === 'oidc';
}

export function getSettingsPolicyError(
  flow: Pick<SettingsFlow, 'ui'> | undefined
): 'session_not_privileged' | 'insufficient_aal' | null {
  if (hasFlowMessageId(flow, BIFROST_ERR_SESSION_NOT_PRIVILEGED)) {
    return 'session_not_privileged';
  }

  if (hasFlowMessageId(flow, BIFROST_ERR_INSUFFICIENT_AAL)) {
    return 'insufficient_aal';
  }

  return null;
}

export function flowIsExpired(flow: OryFlow, bufferMs = 0): boolean {
  if (!('expires_at' in flow) || !flow.expires_at) {
    return false;
  }

  const expiresAt = new Date(flow.expires_at).getTime();
  const nowWithBuffer = Date.now() + bufferMs;

  return nowWithBuffer >= expiresAt;
}

export type OryFlow =
  | LoginFlow
  | LogoutFlow
  | RecoveryFlow
  | SettingsFlow
  | VerificationFlow;
export function isFlow<T extends LoginFlow>(flow: unknown): flow is T {
  const f = flow as T;
  return 'type' in f && 'id' in f && 'state' in f;
}

export function isSessionRefreshRequiredError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  if (error.response?.status !== 403) return false;

  const data = error.response?.data;
  const errorId = data?.error?.id;

  return errorId === 'session_refresh_required';
}
